import * as React from "react"
import { useState } from "react"
import gql from "graphql-tag"
import { useQuery } from "react-apollo"
import * as Yup from "yup"
import {
  Button,
  Text,
  useShowSuccessToast,
  RadioButtonConnectedField,
  InputConnectedField,
  ThemeCss,
  TextAreaConnectedField,
} from "gatsby-interface"
import {
  SettingsCardContent,
  SettingsCardEditButton,
} from "@modules/site/settings"
import { MdArrowForward } from "react-icons/md"
import { visitorAccess as text } from "@modules/locales/default.js"
import { useTracker } from "@modules/analytics"
import { SiteSettingsSection } from "../constants"
import SiteSettingsSectionCard from "./SiteSettingsSectionCard"
import { PreviewProtectionType } from "@modules/graphql/types"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { visitorAccessOptions } from "../constants"
import { updateVisitorAccessCache } from "./VisitorAccess.helpers"
import Form from "../../../form/components/Form"
import CopyUrl from "../../../ui/components/CopyUrl"
import { FadeOnMount } from "@modules/animation"
import { FormattedMessage } from "@modules/locales"
import { useSetSitePreviewProtectionMutation } from "../queries.generated"
import { useWorkspaceDetailsShortcuts } from "@modules/organization/builds/hooks/useWorkspaceDetailsShortcuts"

const PREVIEW_AUTH_TOKEN = gql`
  query sitePreviewAuthToken($siteId: UUID!) {
    sitePreviewAuthToken(siteId: $siteId)
  }
`

function PreviewAuthenticationSection({ siteId }: { siteId: string }) {
  const { data } = useQuery(PREVIEW_AUTH_TOKEN, {
    variables: {
      siteId,
    },
  })
  const token = data && data.sitePreviewAuthToken

  return (
    <div css={webhookSectionCss}>
      <Text css={webhookTextCss}>
        Your Preview instance is not accessible to the public. If you need to
        bypass the visitor access check, please set the HTTP header below before
        making requests to your Preview instance URL
      </Text>

      {token && <CopyUrl url={token} urlDisplay={`Authorization: ${token}`} />}
    </div>
  )
}

export type VisitorAccessProps = {
  siteId: string
  organizationId: string
  previewProtectionIpRanges: string
  sitePreviewProtection: PreviewProtectionType
  sitePreviewPassword: string
}

export function VisitorAccess({
  siteId,
  organizationId,
  previewProtectionIpRanges,
  sitePreviewPassword,
  sitePreviewProtection,
}: VisitorAccessProps) {
  const { trackButtonClicked } = useTracker()
  const [formVisible, setFormVisible] = useState(false)
  const [previewIpRanges, setPreviewIpRanges] = React.useState<string>(
    previewProtectionIpRanges
  )

  const currentOption = visitorAccessOptions.find(
    option => option.value === sitePreviewProtection
  )

  return (
    <SiteSettingsSectionCard
      id={SiteSettingsSection.VisitorAccess}
      renderTitle={defaultTitle =>
        !formVisible ? defaultTitle : text.editVisitorAccess
      }
    >
      <SettingsCardEditButton
        label={text.editAccess}
        onClick={() => {
          trackButtonClicked(text.editAccess, {
            uiSource: `Site Settings`,
          })
          return setFormVisible(!formVisible)
        }}
      />
      <SettingsCardContent>
        {formVisible ? (
          <VisitorAccessForm
            organizationId={organizationId}
            siteId={siteId}
            sitePreviewProtection={sitePreviewProtection}
            sitePreviewPassword={sitePreviewPassword}
            previewIpRanges={previewIpRanges}
            setPreviewIpRanges={setPreviewIpRanges}
            onSuccess={() => setFormVisible(false)}
            onCancel={() => setFormVisible(false)}
          />
        ) : (
          <React.Fragment>
            <div css={rowCss}>
              <div css={labelCss}>{currentOption?.label}</div>
              {sitePreviewProtection === PreviewProtectionType.IpRanges &&
              previewIpRanges ? (
                <div css={valueCss}>
                  <FormattedMessage<"ipList">
                    css={valueCss}
                    values={{ ipList: previewIpRanges }}
                    message={currentOption?.descriptionSelected || ``}
                  />
                </div>
              ) : (
                <div css={valueCss}>{currentOption?.description}</div>
              )}
            </div>
            {sitePreviewProtection !== PreviewProtectionType.Public &&
              sitePreviewProtection !== PreviewProtectionType.IpRanges && (
                <PreviewAuthenticationSection siteId={siteId} />
              )}
          </React.Fragment>
        )}
      </SettingsCardContent>
    </SiteSettingsSectionCard>
  )
}

const validationSchema = Yup.object().shape({
  previewProtect: Yup.string().required(`Visitor Access Type is required`),
  previewPass: Yup.string().test(
    `previewPass`,
    text.cantBeEmpty,
    function (val) {
      const previewProtect = this.parent.previewProtect

      if (previewProtect === `PASSWORD` && !val) {
        return false
      }
      return true
    }
  ),
  ipRanges: Yup.string().test(`ipRanges`, text.cantBeEmpty, function (val) {
    const previewProtect = this.parent.previewProtect

    if (previewProtect === `IP_RANGES` && !val) {
      return false
    }
    return true
  }),
})

export type VisitorAccessFormProps = {
  siteId: string
  organizationId: string
  sitePreviewProtection: PreviewProtectionType
  previewIpRanges: string
  setPreviewIpRanges: any
  sitePreviewPassword: string
  onSuccess: any
  onCancel: any
}

export function VisitorAccessForm({
  siteId,
  organizationId,
  sitePreviewProtection,
  sitePreviewPassword,
  previewIpRanges,
  setPreviewIpRanges,
  onSuccess,
  onCancel,
}: VisitorAccessFormProps) {
  const [setPreviewProtection, { loading }] =
    useSetSitePreviewProtectionMutation()

  const workspace = useWorkspaceDetailsShortcuts(organizationId)
  const ipProtectionAllowed = workspace.isOnEnterprisePlan

  const { trackButtonClicked } = useTracker()
  const showSuccessToast = useShowSuccessToast()
  const [setError, errorAlert] = useTriggerErrorAlert()

  return (
    <Form
      validationSchema={validationSchema}
      onSubmit={values => {
        setError(null)
        trackButtonClicked(`save visitor access`, {
          uiSource: `Site Settings`,
        })
        return setPreviewProtection({
          variables: {
            id: siteId,
            type: values.previewProtect,
            password: values.previewPass || undefined,
            ipRanges: values.ipRanges || undefined,
          },
          update: updateVisitorAccessCache(siteId, values),
        })
          .then(({ data }) => {
            if (!data?.setSitePreviewProtection) {
              return
            }

            const { setSitePreviewProtection } = data

            if (!setSitePreviewProtection.success) {
              throw setSitePreviewProtection
            }

            if (values.previewProtect === PreviewProtectionType.IpRanges) {
              setPreviewIpRanges(setSitePreviewProtection.message)
            } else {
              setPreviewIpRanges(``)
            }

            showSuccessToast(
              <React.Fragment>
                <strong>{text.visitorAccess}</strong> {text.updated}
              </React.Fragment>
            )
          })
          .then(() => onSuccess())
          .catch(err => setError(err))
      }}
      initialValues={{
        previewPass: sitePreviewPassword ? sitePreviewPassword : ``,
        previewProtect: sitePreviewProtection || PreviewProtectionType.Public,
        ipRanges: previewIpRanges || ``,
      }}
    >
      {({ submitForm, values }) => (
        <Form.FormElement applySpacing>
          <RadioButtonConnectedField
            name="previewProtect"
            label="Visitor Access"
            css={theme => ({
              marginBottom: theme.space[6],
            })}
            options={visitorAccessOptions
              .filter(({ value }) =>
                value === PreviewProtectionType.IpRanges
                  ? ipProtectionAllowed
                  : true
              )
              .map(({ label, value, description, descriptionSelected }) => {
                return {
                  value,
                  label: (
                    <div css={optionLabelCss}>
                      <span css={optionNameCss}>{label}:</span>
                      {value === PreviewProtectionType.IpRanges ? (
                        <div css={valueCss}>
                          <FormattedMessage<"ipList">
                            css={valueCss}
                            values={{ ipList: previewIpRanges }}
                            message={
                              (previewIpRanges
                                ? descriptionSelected
                                : description) || ``
                            }
                          />
                        </div>
                      ) : (
                        <small css={valueCss}>{description}</small>
                      )}
                    </div>
                  ),
                }
              })}
          />
          {values.previewProtect === PreviewProtectionType.Password && (
            <FadeOnMount>
              <InputConnectedField
                name="previewPass"
                label="Preview Password"
                type={"password"}
                data-cy="visitor-access-password-input"
              />
            </FadeOnMount>
          )}
          {values.previewProtect === PreviewProtectionType.IpRanges && (
            <FadeOnMount>
              <TextAreaConnectedField
                name="ipRanges"
                label="IP-based"
                data-cy="visitor-access-ip-based-input"
              />
            </FadeOnMount>
          )}
          {errorAlert}
          <Form.Actions>
            <Button
              variant="SECONDARY"
              tone="NEUTRAL"
              type={`reset`}
              onClick={() => {
                trackButtonClicked(`cancel visitor access`, {
                  uiSource: `Site Settings`,
                })
                return onCancel()
              }}
            >
              {text.cancel}
            </Button>
            <Button
              type="button"
              onClick={submitForm}
              loading={loading}
              rightIcon={<MdArrowForward />}
            >
              {text.save}
            </Button>
          </Form.Actions>
        </Form.FormElement>
      )}
    </Form>
  )
}

/* Styles */

const rowCss: ThemeCss = theme => ({
  display: `grid`,

  [theme.mediaQueries.tablet]: {
    gridTemplateColumns: `20% 80%`,
  },
})

const labelCss: ThemeCss = theme => ({
  color: theme.colors.grey[50],
  fontSize: theme.fontSizes[1],
  fontWeight: "normal",
})

const valueCss: ThemeCss = theme => ({
  color: theme.colors.black,
  fontSize: theme.fontSizes[1],
  fontWeight: "normal",
})

const webhookSectionCss: ThemeCss = theme => ({
  marginTop: theme.space[5],
})

const webhookTextCss: ThemeCss = theme => ({
  marginTop: 0,
  marginBottom: theme.space[3],
  color: theme.colors.grey[50],
  fontSize: theme.fontSizes[1],
})

const optionLabelCss: ThemeCss = theme => ({
  display: `flex`,
  flexDirection: `column`,

  [theme.mediaQueries.tablet]: {
    flexDirection: `row`,
    alignItems: `center`,
  },
})

const optionNameCss: ThemeCss = theme => ({
  [theme.mediaQueries.tablet]: {
    marginRight: theme.space[4],
  },
})
